/**
 * The Quote Details money path, extracted from GearListSection.tsx (#81) as
 * pure functions so the highest-risk arithmetic — per-line totals, the
 * gear-only percent-discount base, and the grand total — is unit-testable
 * without mounting the editor. Mirrors buildGroupedQuoteLines server-side.
 */
import {
  addDaysISO,
  bookingItemDays,
  calcGearLineTotal,
  DEFAULT_EXTRA_DAY_RATE,
} from "./format-utils";
import {
  isAvailabilityControlledItem,
  isSingleDayItem,
} from "./booking-item-types";
import type { GearItem } from "./gear-list-types";

/**
 * Multi-day extra-hire-day rate as a fraction: the tenant's
 * multiDayExtraDayPercent / 100, or the app default when unset.
 */
export function gearExtraDayRate(multiDayExtraDayPercent?: number): number {
  return multiDayExtraDayPercent != null
    ? multiDayExtraDayPercent / 100
    : DEFAULT_EXTRA_DAY_RATE;
}

/**
 * Days the server will actually price this row over.
 *
 * `booking_items.days` was dropped in migration 008: the stored hire window is
 * the only persisted day count, and generate_quote_from_gear.go prices off
 * bookingItemDays(starts_on, ends_on). A row's `days` field is that same number
 * surfaced for editing — authoritative ONLY while the row has no window yet
 * (added in the editor), which is exactly when gear_items.go derives the window
 * from it. Pricing off `days` for a row that already has a window is what let
 * the editor's total drift away from the generated quote (#227).
 */
export function gearRowDays(item: GearItem): number {
  if (item.starts_on && item.ends_on) {
    return bookingItemDays(item.starts_on, item.ends_on);
  }
  return item.days > 0 ? item.days : 1;
}

/**
 * A row with its days set, keeping the hire window in step so the edit
 * survives the save. Without this the window is sent back unchanged and
 * gear_items.go — which only derives ends_on when it arrives blank — silently
 * discards the edit (#227).
 *
 * `fallbackStart` is the booking's own start date, used to seat a row that has
 * no window at all; with neither, the row keeps just `days` and the server
 * derives the window on save.
 */
export function gearRowWithDays(
  item: GearItem,
  days: number,
  fallbackStart?: string | null,
): GearItem {
  const next = days > 1 ? days : 1;
  const startsOn = item.starts_on || fallbackStart || null;
  if (!startsOn) return { ...item, days: next };
  return {
    ...item,
    days: next,
    starts_on: startsOn,
    ends_on: addDaysISO(startsOn, next),
  };
}

/**
 * Net line total: qty × days × rate (gear multi-day curve) minus the line's
 * flat $ discount — matches buildGroupedQuoteLines server-side. Service and
 * scope rows carry their whole duration in qty and price single-day (their
 * Days cell renders as a dash) — see isSingleDayItem.
 */
export function netLineTotal(item: GearItem, extraDayRate: number): number {
  const isGear = isAvailabilityControlledItem(item.type);
  const singleDay = isSingleDayItem(item.type);
  return (
    calcGearLineTotal(
      item.hire_rate || 0,
      item.quantity || 1,
      singleDay ? 1 : gearRowDays(item),
      isGear,
      extraDayRate,
    ) - (item.discount > 0 ? item.discount : 0)
  );
}

export interface GearListTotals {
  /** Σ netLineTotal over every visible row — the quote grand total (pre gear-discount). */
  totalCost: number;
  /** Σ netLineTotal over gear-only rows — the base the percent discount applies to. */
  gearSubtotal: number;
  /** gearSubtotal × gearDiscountPercent / 100. */
  gearDiscountAmount: number;
}

/**
 * The money trio for a set of VISIBLE rows (the active package's complete line
 * set on a packaged booking, matching that package's quote PDF). The percent
 * discount only applies to gear lines (net of their own line discounts), never
 * to tech-time / vehicle — mirrors the server tail line.
 *
 * totalCost and gearSubtotal accumulate in row order, byte-identical to the two
 * separate reduces this replaced.
 */
export function gearListTotals(
  visibleItems: GearItem[],
  gearDiscountPercent: number,
  extraDayRate: number,
): GearListTotals {
  let totalCost = 0;
  let gearSubtotal = 0;
  for (const item of visibleItems) {
    const net = netLineTotal(item, extraDayRate);
    totalCost += net;
    if (isAvailabilityControlledItem(item.type)) gearSubtotal += net;
  }
  return {
    totalCost,
    gearSubtotal,
    gearDiscountAmount: (gearSubtotal * gearDiscountPercent) / 100,
  };
}
